import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import ReviewsSection from "../components/ReviewsSection";
import InstagramPreview from "../components/InstagramPreview";
import ShareButton from "../components/ShareButton";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Star, MapPin, Phone, Globe, Mail, Clock, Crown, ArrowLeft, Instagram, Facebook, Navigation, ExternalLink, BadgeCheck, Repeat } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

export default function VenueDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [venue, setVenue] = useState(null);
  const [media, setMedia] = useState({ photos: [], reviews: [], attribution: "" });
  const [venueEvents, setVenueEvents] = useState(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimMsg, setClaimMsg] = useState("");
  const [claimProof, setClaimProof] = useState("");
  const [claimSubmitting, setClaimSubmitting] = useState(false);

  const reloadVenue = () => api.get(`/venues/${id}`).then((r) => setVenue(r.data));

  const track = (channel) => {
    api.post(`/venues/${id}/track-click`, { channel }).catch(() => {});
  };

  useEffect(() => {
    reloadVenue();
    api.get(`/venues/${id}/places-media`).then((r) => setMedia(r.data)).catch(() => {});
    api.get(`/venues/${id}/events`).then((r) => setVenueEvents(r.data)).catch(() => setVenueEvents([]));
    // Track view for the "Suggested for you" rail. Anonymous session id lives
    // in localStorage so the recommendations rail still personalises for
    // logged-out visitors. Fire-and-forget.
    let sid = localStorage.getItem("inairlie_sid");
    if (!sid) {
      sid = `s_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
      localStorage.setItem("inairlie_sid", sid);
    }
    api.post(`/venues/${id}/view`, { session_id: sid }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submitClaim = async () => {
    if (!user) {
      toast.error("Please sign in to claim a venue");
      return;
    }
    setClaimSubmitting(true);
    try {
      await api.post(`/venues/${id}/claim`, { message: claimMsg, proof: claimProof });
      toast.success("Claim submitted — we'll review it within 48h");
      setClaimOpen(false);
      setClaimMsg("");
      setClaimProof("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not submit claim";
      toast.error(msg);
    } finally {
      setClaimSubmitting(false);
    }
  };

  if (!venue) return <Layout><div className="py-20 text-center">Loading...</div></Layout>;

  // Prefer Places photos > stored images > stock fallback
  const placesPhotos = media.photos || [];
  const heroImg = placesPhotos[0]?.url || venue.places_photo_url || venue.images?.[0] || "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1800";
  const galleryPhotos = placesPhotos.slice(1, 5);
  const img = heroImg;
  const ownerless = !venue.created_by;

  // ----- JSON-LD LocalBusiness structured data for Google rich results -----
  // Maps our category slugs to the most specific Schema.org type Google supports.
  const SCHEMA_TYPE = {
    restaurant: "Restaurant",
    cafe: "CafeOrCoffeeShop",
    bar: "BarOrPub",
    nightlife: "NightClub",
    activity: "TouristAttraction",
  };
  const DAY_NAME = {
    mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday",
    fri: "Friday", sat: "Saturday", sun: "Sunday",
  };
  const buildHoursSpec = (hours) => {
    if (!hours) return undefined;
    const out = [];
    for (const [k, v] of Object.entries(hours)) {
      if (!v || v === "closed") continue;
      // Split multi-period hours (e.g. "11:00-14:00,17:30-21:00") into separate Schema entries
      for (const seg of String(v).split(",").map((s) => s.trim()).filter(Boolean)) {
        if (seg.toLowerCase() === "24h") {
          out.push({ "@type": "OpeningHoursSpecification", dayOfWeek: DAY_NAME[k], opens: "00:00", closes: "23:59" });
          continue;
        }
        const [opens, closes] = seg.split("-");
        if (opens && closes) {
          out.push({ "@type": "OpeningHoursSpecification", dayOfWeek: DAY_NAME[k], opens, closes });
        }
      }
    }
    return out.length ? out : undefined;
  };
  const ldImages = [];
  if (img) ldImages.push(img);
  if (Array.isArray(placesPhotos)) {
    for (const p of placesPhotos.slice(0, 6)) {
      if (p?.url && !ldImages.includes(p.url)) ldImages.push(p.url);
    }
  }
  const localBusinessLd = {
    "@context": "https://schema.org",
    "@type": SCHEMA_TYPE[venue.category] || "LocalBusiness",
    "@id": typeof window !== "undefined" ? `${window.location.origin}/venue/${venue.id}` : undefined,
    name: venue.name,
    description: venue.description || undefined,
    url: typeof window !== "undefined" ? `${window.location.origin}/venue/${venue.id}` : undefined,
    image: ldImages.length ? ldImages : undefined,
    telephone: venue.phone || undefined,
    address: venue.address
      ? {
          "@type": "PostalAddress",
          streetAddress: venue.address,
          addressLocality: venue.location || "Airlie Beach",
          addressRegion: "QLD",
          addressCountry: "AU",
        }
      : undefined,
    geo:
      typeof venue.latitude === "number" && typeof venue.longitude === "number"
        ? { "@type": "GeoCoordinates", latitude: venue.latitude, longitude: venue.longitude }
        : undefined,
    aggregateRating:
      venue.rating && venue.review_count
        ? {
            "@type": "AggregateRating",
            ratingValue: Number(venue.rating).toFixed(1),
            reviewCount: venue.review_count,
            bestRating: 5,
            worstRating: 1,
          }
        : undefined,
    openingHoursSpecification: buildHoursSpec(venue.hours),
    sameAs: [venue.website, venue.instagram, venue.facebook].filter(Boolean),
  };
  // Strip undefined fields to keep the payload clean.
  Object.keys(localBusinessLd).forEach((k) => localBusinessLd[k] === undefined && delete localBusinessLd[k]);

  // Safe-stringify JSON-LD: escape `</` so a venue name containing the literal
  // string "</script>" can never break out of the embedded <script> tag and
  // execute injected content. JSON.stringify on a controlled object is the
  // only thing rendered here.
  const ldJson = JSON.stringify(localBusinessLd).replace(/<\/(script)/gi, "<\\/$1");

  return (
    <Layout>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: ldJson }}
      />
      <div className="relative h-[60vh] overflow-hidden">
        <img src={img} alt={venue.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute top-6 left-6">
          <Link to={`/directory/${venue.category}`} className="bg-white/90 backdrop-blur rounded-full px-4 py-2 text-sm text-navy inline-flex items-center gap-2 hover:bg-white transition-colors" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-7xl mx-auto">
            {venue.is_sponsored && (
              <span className="inline-flex items-center gap-1 sponsored-ribbon text-xs px-3 py-1 rounded-full mb-3">
                <Crown className="w-3 h-3" /> Sponsored
              </span>
            )}
            <h1 className="font-heading text-4xl lg:text-6xl text-white tracking-tight" data-testid="venue-name">{venue.name}</h1>
            <div className="flex flex-wrap items-center gap-4 text-white/90 mt-3">
              <span className="capitalize">{venue.category}</span>
              <span>·</span>
              <span className="flex items-center gap-1"><Star className="w-4 h-4 text-sponsored fill-sponsored" /> {(venue.rating || 0).toFixed(1)} ({venue.review_count || 0})</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <h2 className="font-heading text-2xl text-navy mb-4">About</h2>
          <p className="text-neutral-700 leading-relaxed text-lg mb-8">{venue.description}</p>

          {venueEvents && venueEvents.length > 0 && (
            <section className="mb-10" data-testid="venue-events">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-2xl text-navy">What&apos;s on at {venue.name}</h2>
                <span className="text-xs text-coastal uppercase tracking-widest">{venueEvents.length} upcoming</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {venueEvents.slice(0, 6).map((e) => (
                  <Link
                    key={e.id}
                    to={`/events/${e.id}`}
                    className="group flex gap-4 bg-white rounded-2xl border border-black/5 p-4 hover:shadow-md transition-shadow"
                    data-testid={`venue-event-${e.id}`}
                  >
                    {e.image ? (
                      <img
                        src={e.image.startsWith("http") ? e.image : e.image}
                        alt=""
                        className="w-24 h-24 rounded-xl object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-xl bg-sand/40 flex-shrink-0 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-navy/40" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] tracking-widest text-coastal uppercase flex items-center gap-1.5">
                        {e.is_recurring ? (
                          <><Repeat className="w-3 h-3" /> {e.recurrence === "weekly" ? "Weekly" : e.recurrence === "biweekly" ? "Fortnightly" : "Monthly"}</>
                        ) : (
                          new Date(e.start_time).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })
                        )}
                      </div>
                      <div className="font-heading text-lg text-navy mt-1 line-clamp-2 group-hover:underline">{e.title}</div>
                      <div className="text-xs text-neutral-500 mt-1">
                        {new Date(e.start_time).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
                        {e.price ? <span className="ml-2 text-navy font-medium">· {e.price}</span> : null}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {galleryPhotos.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 mb-10">
              {galleryPhotos.map((p, i) => (
                <div key={p.url || `gallery-${i}`} className="aspect-[4/3] rounded-2xl overflow-hidden bg-sand/30">
                  <img src={p.url} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
              ))}
            </div>
          ) : (venue.images?.length > 1 && (
            <div className="grid grid-cols-2 gap-4 mb-10">
              {venue.images.slice(1, 5).map((src, i) => (
                <div key={src || `img-${i}`} className="aspect-[4/3] rounded-2xl overflow-hidden">
                  <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
          ))}

          {/* Google reviews — live, never cached */}
          {media.reviews?.length > 0 && (
            <section className="mb-10" data-testid="google-reviews">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-2xl text-navy">What people are saying on Google</h2>
                <span className="text-xs text-neutral-400">{media.attribution}</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {media.reviews.slice(0, 4).map((r, i) => (
                  <div key={`${r.author || "rev"}-${r.relative_time || i}`} className="bg-white rounded-2xl p-5 border border-black/5">
                    <div className="flex items-center gap-3 mb-2">
                      {r.photo ? (
                        <img src={r.photo} alt={r.author} className="w-9 h-9 rounded-full" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-sand/60 flex items-center justify-center text-sm text-navy font-medium">{r.author[0]}</div>
                      )}
                      <div>
                        <div className="text-sm font-medium text-navy">{r.author}</div>
                        <div className="text-xs text-neutral-500 flex items-center gap-1">
                          <span className="flex">{Array.from({ length: 5 }).map((_, idx) => (
                            <Star key={`star-${idx}`} className={`w-3 h-3 ${idx < (r.rating || 0) ? "fill-sponsored text-sponsored" : "text-neutral-200"}`} />
                          ))}</span>
                          <span>· {r.relative_time}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-neutral-600 leading-relaxed line-clamp-5">{r.text}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          <InstagramPreview venue={venue} />

          <ReviewsSection venueId={venue.id} onRatingChange={reloadVenue} />
        </div>

        <aside className="space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm">
            <h3 className="font-heading text-lg text-navy mb-4">Visit</h3>
            <div className="space-y-3 text-sm">
              {venue.address && <div className="flex items-start gap-3"><MapPin className="w-4 h-4 text-coastal mt-0.5 shrink-0" /><span>{venue.address}</span></div>}
              {venue.phone && <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-coastal shrink-0" /><a href={`tel:${venue.phone}`} className="hover:text-navy">{venue.phone}</a></div>}
              {venue.email && <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-coastal shrink-0" /><a href={`mailto:${venue.email}`} className="hover:text-navy">{venue.email}</a></div>}
              {venue.instagram && (
                <div className="flex items-center gap-3" data-testid="venue-instagram">
                  <Instagram className="w-4 h-4 text-coastal shrink-0" />
                  <a href={venue.instagram} target="_blank" rel="noreferrer" onClick={() => track("instagram")} className="hover:text-navy break-all">
                    {venue.instagram.replace(/^https?:\/\/(www\.)?instagram\.com\//, "@").replace(/\/$/, "")}
                  </a>
                </div>
              )}
              {venue.facebook && (
                <div className="flex items-center gap-3" data-testid="venue-facebook">
                  <Facebook className="w-4 h-4 text-coastal shrink-0" />
                  <a href={venue.facebook} target="_blank" rel="noreferrer" onClick={() => track("facebook")} className="hover:text-navy break-all">
                    {venue.facebook.replace(/^https?:\/\/(www\.)?facebook\.com\//, "").replace(/\/$/, "")}
                  </a>
                </div>
              )}
            </div>

            {/* CTAs */}
            {(venue.website || venue.address || (venue.latitude && venue.longitude)) && (
              <div className="mt-5 pt-5 border-t border-black/5 space-y-2">
                {venue.website && (
                  <a
                    href={venue.website}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => track("website")}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-navy text-white text-sm font-medium hover:bg-navy/90 transition-colors"
                    data-testid="visit-website-cta"
                  >
                    <Globe className="w-4 h-4" /> Visit website <ExternalLink className="w-3 h-3 opacity-70" />
                  </a>
                )}
                {(venue.address || (venue.latitude && venue.longitude)) && (
                  <a
                    href={
                      venue.latitude && venue.longitude
                        ? `https://www.google.com/maps/dir/?api=1&destination=${venue.latitude},${venue.longitude}`
                        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${venue.name} ${venue.address || venue.location || ""}`)}`
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white border border-navy/20 text-navy text-sm font-medium hover:bg-sand/40 transition-colors"
                    data-testid="open-in-maps-cta"
                  >
                    <Navigation className="w-4 h-4" /> Open in Google Maps
                  </a>
                )}
                <ShareButton
                  title={venue.name}
                  text={venue.description}
                  label="Share this venue"
                  className="w-full"
                  testId="venue-share-btn"
                />
              </div>
            )}
          </div>

          {/* Claim this venue (owner-less) */}
          {ownerless && (
            <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
              <DialogTrigger asChild>
                <button
                  className="w-full bg-sand/40 hover:bg-sand/60 border border-sand text-navy rounded-2xl p-5 text-left transition-colors"
                  data-testid="claim-venue-btn"
                >
                  <div className="flex items-center gap-2 text-sm font-medium mb-1">
                    <BadgeCheck className="w-4 h-4 text-coastal" /> Is this your venue?
                  </div>
                  <p className="text-xs text-neutral-600">Claim it to edit details, add photos, run sponsorship campaigns and reply to reviews.</p>
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Claim {venue.name}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-neutral-600">
                  Tell us briefly who you are and what role you have at the venue. We&apos;ll verify and approve within 48h.
                </p>
                <Textarea
                  rows={4}
                  placeholder="I'm Sam, the owner. You can verify me at sam@boatysairliebeach.com or on (07) 4948 2000."
                  value={claimMsg}
                  onChange={(e) => setClaimMsg(e.target.value)}
                  maxLength={1000}
                  data-testid="claim-message"
                />
                <input
                  type="url"
                  placeholder="Optional: a URL proving ownership (your About page, ABN lookup, etc.)"
                  value={claimProof}
                  onChange={(e) => setClaimProof(e.target.value)}
                  className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm"
                  data-testid="claim-proof"
                />
                <Button
                  onClick={submitClaim}
                  disabled={claimSubmitting || !claimMsg.trim()}
                  className="w-full rounded-full bg-navy hover:bg-navy/90"
                  data-testid="claim-submit"
                >
                  {claimSubmitting ? "Submitting…" : "Submit claim"}
                </Button>
                {!user && (
                  <p className="text-xs text-coastal text-center">
                    You&apos;ll need to <Link to="/login" className="underline">sign in</Link> first.
                  </p>
                )}
              </DialogContent>
            </Dialog>
          )}


          {venue.hours && (
            <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm">
              <h3 className="font-heading text-lg text-navy mb-4 flex items-center gap-2"><Clock className="w-4 h-4" /> Opening hours</h3>
              <div className="space-y-2 text-sm">
                {DAYS.map((d) => (
                  <div key={d.key} className="flex justify-between">
                    <span className="text-neutral-500">{d.label}</span>
                    <span className="font-medium">{venue.hours[d.key] || "Closed"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </Layout>
  );
}
